import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BarRows from '../components/charts/BarRows';
import { ErrorBanner, InfoBanner } from '../components/Feedback';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

/**
 * Module 12 — the Sales dashboard.
 *
 * <p>Everything here is the representative's own: their schools, the teachers routed to
 * them, the code stock sitting with their schools, and the windows about to close. The
 * narrowing happens in the database, not in this component — there is no wider figure
 * on the wire for a curious reader to find.
 */
export default function SalesDashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.salesDashboard().then(setData).catch(setError);
  }, []);

  return (
    <>
      <div className="page-head">
        <h1>Dashboard</h1>
        <p>Signed in as {user?.roleLabel}. Your schools, your teachers, your stock.</p>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />
      {!data && !error && <div className="empty">Loading…</div>}

      {data && (
        <>
          <div className="kpi-row">
            <Kpi label="My schools" value={data.mySchools} note={`${data.myTeachers} teachers`} />
            <Kpi label="Awaiting my decision" value={data.awaitingMyDecision}
                 note={data.awaitingMyDecision === 0 ? 'Nothing waiting on you' : 'Teachers routed to you'} />
            <Kpi label="Codes to distribute" value={data.codesWithMySchools}
                 note={`${data.codesUsedAtMySchools.toLocaleString()} already redeemed`} />
            <Kpi label="Renewals due" value={data.renewalsDue}
                 note={data.expired > 0 ? `${data.expired} already expired` : 'Within 30 days'} />
          </div>

          {data.mySchools === 0 && (
            <InfoBanner>
              No states are assigned to you yet. Once they are, this dashboard fills in —
              until then there is nothing for it to show.
            </InfoBanner>
          )}

          {data.awaitingMyDecision > 0 && (
            <InfoBanner>
              <strong>{data.awaitingMyDecision}</strong> teacher
              {data.awaitingMyDecision === 1 ? '' : 's'} routed to you and waiting.{' '}
              <Link to="/verification">Open the queue</Link>.
            </InfoBanner>
          )}

          <div className="panel-grid">
            <section className="card card-pad panel">
              <h2>Code stock by school</h2>
              <p className="panel-sub">
                Allocated but not yet redeemed. A school at zero cannot hand out any more.
              </p>
              <BarRows
                slices={data.stockBySchool}
                emptyMessage="None of your schools hold undistributed codes."
              />
              <p className="hint">
                <Link to="/codes">Allocate more codes</Link>
              </p>
            </section>

            <section className="card card-pad panel">
              <h2>My schools</h2>
              <p className="panel-sub">By customer status.</p>
              <BarRows
                slices={data.schoolsByStatus}
                emptyMessage="No schools in your states yet."
              />
              <p className="hint">
                <Link to="/schools">Open my schools</Link>
              </p>
            </section>

            <section className="card card-pad panel">
              <h2>Teachers</h2>
              <p className="panel-sub">Those routed to you for a decision.</p>
              <BarRows
                slices={[
                  { key: 'ASSIGNED', label: 'Awaiting my decision', value: data.awaitingMyDecision },
                  { key: 'VERIFIED', label: 'Verified by me', value: data.verifiedByMe },
                ]}
                emptyMessage="No teachers have been routed to you yet."
              />
            </section>

            <section className="card card-pad panel">
              <h2>Student access</h2>
              <p className="panel-sub">
                Books at your schools whose 15-month window is closing.
              </p>
              <BarRows
                slices={[
                  { key: 'RENEWAL_DUE', label: 'Renewal due', value: data.renewalsDue },
                  { key: 'EXPIRED', label: 'Expired', value: data.expired },
                ]}
                emptyMessage="Nothing is close to expiring."
              />
              <p className="hint">
                <Link to="/reports">Open the reports</Link>
              </p>
            </section>
          </div>

          <p className="tiny muted" style={{ marginTop: 'var(--s2)' }}>
            Counted at {new Date(data.generatedAt).toLocaleTimeString()}.
          </p>
        </>
      )}
    </>
  );
}

function Kpi({ label, value, note }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value.toLocaleString()}</div>
      {note && <div className="kpi-note">{note}</div>}
    </div>
  );
}
